import { createColumnHelper } from '@tanstack/react-table';
import * as z from 'zod';

import { SkuBadge } from '@/pages/seller/components/sku-badge';
import { formatNumber } from '@/shared/helpers';

export type AsnLine = {
  id: string;
  skuId: string;
  skuName: string;
  description: string;
  qtyExpected: number;
  qtyReceived?: number | null;
  unitPurchaseCost?: number | null;
  unitCostTotal?: number | null;
};

export type AsnLineRow = Omit<AsnLine, 'qtyExpected'> & {
  qtyExpected?: number;
};

export type AsnLineAddInput = Omit<AsnLine, 'id' | 'qtyReceived'>;

export type AsnLineUpdatePatch = Partial<
  Pick<AsnLine, 'description' | 'qtyExpected' | 'unitPurchaseCost' | 'unitCostTotal'>
>;

const columnHelper = createColumnHelper<AsnLineRow>();

type AsnLineColumnsOptions = {
  showQtyReceived?: boolean;
  isRecosting?: boolean;
};

function roundNumber(value: number, precision: number) {
  const factor = 10 ** precision;
  return Math.round((value + Number.EPSILON) * factor) / factor;
}

function getEffectiveQuantity(line: Partial<AsnLineRow>) {
  // The backend initializes qtyReceived to 0, so fall back to expected quantity until received is positive.
  return line.qtyReceived || line.qtyExpected || 0;
}

export function calculateUnitCostTotal(line: Partial<AsnLineRow>) {
  return roundNumber((line.unitPurchaseCost ?? 0) * getEffectiveQuantity(line), 2);
}

export function calculateUnitPurchaseCost(line: Partial<AsnLineRow>) {
  const quantity = getEffectiveQuantity(line);
  if (quantity === 0) return 0;

  return roundNumber((line.unitCostTotal ?? 0) / quantity, 6);
}

function formatOptionalNumber(value: number | null | undefined, precision = 2) {
  if (value === null || value === undefined) return undefined;
  return formatNumber(value, precision);
}

export const asnLineEditableSchema = z.object({
  description: z.string().min(1, 'Description is required'),
  qtyExpected: z.number('Expected quantity is required').int().nonnegative(),
  unitPurchaseCost: z.number().nonnegative().nullable(),
  unitCostTotal: z.number().nonnegative().nullable(),
});

export const asnLineRecostEditableSchema = z.object({
  unitPurchaseCost: z.number().nonnegative(),
  unitCostTotal: z.number().nonnegative(),
});

export type AsnLineEditableValues = z.output<typeof asnLineEditableSchema>;
export type AsnLineRecostEditableValues = z.output<typeof asnLineRecostEditableSchema>;

export function getAsnLineColumns({
  showQtyReceived = false,
  isRecosting = false,
}: AsnLineColumnsOptions = {}) {
  return [
    columnHelper.accessor('description', {
      header: 'SKU / description',
      cell: ({ getValue, row }) => (
        <div className="flex min-w-0 items-center gap-2">
          <SkuBadge name={row.original.skuName} />
          <span className="truncate">{getValue()}</span>
        </div>
      ),
      meta: {
        truncate: true,
        ...(isRecosting
          ? {}
          : {
              editable: {
                label: 'Description',
                editor: {
                  type: 'text',
                },
              },
            }),
      },
    }),
    columnHelper.accessor('qtyExpected', {
      header: 'Qty expected',
      cell: ({ getValue }) => formatOptionalNumber(getValue(), 0),
      meta: {
        align: 'right',
        ...(isRecosting
          ? {}
          : {
              editable: {
                label: 'Qty expected',
                editor: {
                  type: 'number',
                  min: 0,
                  step: 1,
                },
              },
            }),
      },
    }),
    ...(showQtyReceived
      ? [
          columnHelper.accessor('qtyReceived', {
            header: 'Qty received',
            cell: ({ getValue }) => formatOptionalNumber(getValue(), 0),
            meta: {
              align: 'right',
            },
          }),
        ]
      : []),
    columnHelper.accessor('unitPurchaseCost', {
      header: 'Unit cost',
      cell: ({ getValue }) => formatOptionalNumber(getValue(), 6),
      meta: {
        align: 'right',
        editable: {
          label: 'Unit cost',
          editor: {
            type: 'number',
            min: 0,
            step: 'any',
          },
        },
      },
    }),
    columnHelper.accessor('unitCostTotal', {
      header: 'Line total',
      cell: ({ getValue }) => formatOptionalNumber(getValue(), 2),
      meta: {
        align: 'right',
        editable: {
          label: 'Line total',
          editor: {
            type: 'number',
            min: 0,
            step: 0.01,
          },
        },
      },
    }),
  ];
}
